import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2, Copy, Building, Smartphone, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');
  
  // Get manual payment info from location state
  const manualPaymentState = location.state;
  const isManualPayment = manualPaymentState?.paymentMethod === 'manual';

  useEffect(() => {
    if (isManualPayment) {
      // Manual payment - show success immediately
      setStatus('manual_success');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (!sessionId) {
        setStatus('error');
        return;
      }

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/payments/status/${sessionId}`);
        setPaymentData(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Payment status error:', error);
        setStatus('error');
      }
    };

    pollPaymentStatus();
  }, [sessionId, isManualPayment]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const paymentInfo = manualPaymentState?.paymentInfo;

  return (
    <div className="min-h-screen bg-cream-100 flex items-center justify-center py-12 px-4" data-testid="checkout-success-page">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-saffron-500 mx-auto mb-4 animate-spin" />
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Processing Payment...
              </h2>
              <p className="text-gray-600">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                Payment Successful!
              </h2>
              <p className="text-gray-600 mb-6">
                Thank you for your purchase. Your order has been confirmed.
              </p>
              {paymentData && (
                <p className="text-sm text-gray-500 mb-6">
                  Amount paid: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </p>
              )}
              <div className="space-y-3">
                <Button onClick={() => navigate('/orders')} className="w-full btn-primary">
                  View Orders
                </Button>
                <Button onClick={() => navigate('/shop')} variant="outline" className="w-full btn-outline">
                  Continue Shopping
                </Button>
              </div>
            </div>
          )}

          {status === 'manual_success' && (
            <div>
              <div className="text-center mb-6">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                  Order Placed Successfully!
                </h2>
                <p className="text-gray-600">
                  Please complete your payment using one of the methods below.
                </p>
                {manualPaymentState?.orderId && (
                  <p className="text-sm text-saffron-600 font-medium mt-2">
                    Order ID: {manualPaymentState.orderId}
                  </p>
                )}
              </div>

              {/* Payment Amount */}
              <div className="bg-saffron-50 rounded-lg p-4 mb-6 text-center">
                <p className="text-gray-600 text-sm">Amount to Pay</p>
                <p className="text-3xl font-bold text-saffron-600">
                  ${manualPaymentState?.total?.toFixed(2) || '0.00'}
                </p>
              </div>

              {/* Payment Options */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-saffron-600" />
                  Payment Options
                </h3>

                {/* Bank Transfer */}
                {paymentInfo?.bank_name && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Building className="w-5 h-5 text-royal-600" />
                      <span className="font-medium">Bank Transfer</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Bank Name:</span>
                        <span className="font-medium">{paymentInfo.bank_name}</span>
                      </div>
                      {paymentInfo.account_holder && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Account Holder:</span>
                          <span className="font-medium">{paymentInfo.account_holder}</span>
                        </div>
                      )}
                      {paymentInfo.account_number && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Account Number:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{paymentInfo.account_number}</span>
                            <button
                              onClick={() => copyToClipboard(paymentInfo.account_number, 'Account number')}
                              className="text-saffron-600 hover:text-saffron-700"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      {paymentInfo.routing_number && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Routing Number:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{paymentInfo.routing_number}</span>
                            <button
                              onClick={() => copyToClipboard(paymentInfo.routing_number, 'Routing number')}
                              className="text-saffron-600 hover:text-saffron-700"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Digital Payments */}
                {(paymentInfo?.zelle_phone || paymentInfo?.paypal_username || paymentInfo?.venmo_username) && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Smartphone className="w-5 h-5 text-royal-600" />
                      <span className="font-medium">Digital Payments</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      {paymentInfo.zelle_phone && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Zelle:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{paymentInfo.zelle_phone}</span>
                            <button
                              onClick={() => copyToClipboard(paymentInfo.zelle_phone, 'Zelle info')}
                              className="text-saffron-600 hover:text-saffron-700"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      {paymentInfo.paypal_username && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">PayPal:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{paymentInfo.paypal_username}</span>
                            <button
                              onClick={() => copyToClipboard(paymentInfo.paypal_username, 'PayPal info')}
                              className="text-saffron-600 hover:text-saffron-700"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      {paymentInfo.venmo_username && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Venmo:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{paymentInfo.venmo_username}</span>
                            <button
                              onClick={() => copyToClipboard(paymentInfo.venmo_username, 'Venmo info')}
                              className="text-saffron-600 hover:text-saffron-700"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Instructions */}
                {paymentInfo?.instructions && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-blue-800 font-medium mb-2">Important Instructions</p>
                    <p className="text-blue-700 text-sm whitespace-pre-wrap">{paymentInfo.instructions}</p>
                  </div>
                )}

                {/* Order ID reminder */}
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <p className="text-yellow-800 text-sm">
                    <strong>Important:</strong> Please include your Order ID ({manualPaymentState?.orderId}) 
                    in the payment memo/description so we can match your payment with your order.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 mt-6">
                <Button onClick={() => navigate('/orders')} className="w-full btn-primary">
                  View My Orders
                </Button>
                <Button onClick={() => navigate('/shop')} variant="outline" className="w-full btn-outline">
                  Continue Shopping
                </Button>
              </div>
            </div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
              </h2>
              <p className="text-gray-600 mb-6">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.'
                  : 'There was an issue processing your payment. Please check your order or try again.'}
              </p>
              <div className="space-y-3">
                <Button onClick={() => navigate('/cart')} className="w-full btn-primary">
                  Return to Cart
                </Button>
                <Button onClick={() => navigate('/orders')} variant="outline" className="w-full btn-outline">
                  Check Orders
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccessPage;
